import { UNITS, type Unit } from "../types/entities.js";

/**
 * Ergebnis der Schnelleingabe. `name` ist der reine Artikelname ohne Menge,
 * `normalizedName` der Schlüssel für den Abgleich mit dem Familienkatalog.
 */
export interface ParsedItemInput {
  name: string;
  normalizedName: string;
  quantity: number | null;
  unit: Unit | null;
  note: string | null;
}

/** Schreibweisen, die Menschen tippen, auf die Einheiten der App abgebildet. */
const UNIT_ALIASES: Record<string, Unit> = {
  stk: "stk",
  stck: "stk",
  stueck: "stk",
  stück: "stk",
  x: "stk",
  g: "g",
  gr: "g",
  gramm: "g",
  kg: "kg",
  kilo: "kg",
  kilogramm: "kg",
  ml: "ml",
  l: "l",
  liter: "l",
  pck: "pkg",
  pkg: "pkg",
  packung: "pkg",
  packungen: "pkg",
  paket: "pkg",
  dose: "dose",
  dosen: "dose",
  glas: "glas",
  gläser: "glas",
  glaeser: "glas",
  flasche: "flasche",
  flaschen: "flasche",
  bund: "bund",
  becher: "becher",
};

const UNIT_PATTERN = Object.keys(UNIT_ALIASES)
  .sort((a, b) => b.length - a.length)
  .join("|");

// "2 kg", "500g", "1,5 l", "3x", "2 Packungen" am Anfang der Eingabe.
const LEADING_QUANTITY = new RegExp(`^\\s*(\\d+(?:[.,]\\d+)?)\\s*(${UNIT_PATTERN})?\\b\\s*`, "iu");
// "Milch 2", "Eier 10" am Ende der Eingabe.
const TRAILING_QUANTITY = /\s+(\d+(?:[.,]\d+)?)\s*$/u;

/**
 * Gesprochene Mengen. Die Transkription schreibt "eine Gurke" und "zwei
 * Tomaten", nicht "1 Gurke". Bis zum 16.10.2026 kannte der Parser nur
 * Ziffern: "eine Gurke" war ein Artikelname, und nach einem Komma wurde
 * daraus sogar die Notiz des vorigen Artikels.
 */
const ZAHLWOERTER: Record<string, number> = {
  ein: 1,
  eine: 1,
  einen: 1,
  zwei: 2,
  drei: 3,
  vier: 4,
  fünf: 5,
  sechs: 6,
  sieben: 7,
  acht: 8,
  neun: 9,
  zehn: 10,
  elf: 11,
  zwölf: 12,
  zwanzig: 20,
  dreißig: 30,
  fünfzig: 50,
  hundert: 100,
};

const ZAHLWORT_PATTERN = Object.keys(ZAHLWOERTER)
  .sort((a, b) => b.length - a.length)
  .join("|");

// "eine Gurke", "zwei Packungen Milch", "ein Liter Milch". Die Einheit braucht
// ein Leerzeichen danach, sonst wäre das "g" in "ein Glas" schon eine.
const LEADING_NUMBER_WORD = new RegExp(`^\\s*(${ZAHLWORT_PATTERN})\\s+(?:(${UNIT_PATTERN})\\s+)?`, "iu");
const NUMBER_WORD_START = new RegExp(`^(?:${ZAHLWORT_PATTERN})\\s+\\p{L}`, "iu");

/** Beginnt der Text mit einer gesprochenen Menge ("zwei Tomaten")? */
export function startsWithNumberWord(text: string): boolean {
  return NUMBER_WORD_START.test(text.trim());
}

function toNumber(raw: string): number {
  return Number.parseFloat(raw.replace(",", "."));
}

/**
 * Normalisiert einen Artikelnamen für Vergleiche: klein, ohne doppelte Leerzeichen,
 * ohne Klammerzusatz, ohne Satzzeichen am Rand. Umlaute bleiben erhalten, weil der
 * Katalog deutsch ist und "Müsli" und "Musli" verschiedene Dinge sein dürfen.
 */
export function normalizeName(value: string): string {
  return value
    .toLocaleLowerCase("de-DE")
    .replace(/\(.*?\)/gu, " ")
    .replace(/[^\p{L}\p{N}\s%.-]/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}

/**
 * Zerlegt eine Schnelleingabe in Menge, Einheit, Name und Notiz.
 *
 * Beispiele:
 *   "2 kg Kartoffeln"        -> 2 kg Kartoffeln
 *   "3x Milch"               -> 3 stk Milch
 *   "500g Mehl, gesiebt"     -> 500 g Mehl, Notiz "gesiebt"
 *   "Eier 10"                -> 10 Eier
 *   "Butter (Bio)"           -> Butter, Notiz "Bio"
 */
export function parseItemInput(raw: string): ParsedItemInput {
  let rest = raw.trim().replace(/\s+/gu, " ");
  let quantity: number | null = null;
  let unit: Unit | null = null;
  let note: string | null = null;

  const parenthesis = rest.match(/\((.+?)\)/u);
  if (parenthesis?.[1]) {
    note = parenthesis[1].trim();
    rest = rest.replace(parenthesis[0], " ").replace(/\s+/gu, " ").trim();
  }

  const comma = rest.indexOf(",");
  if (comma > 0) {
    const tail = rest.slice(comma + 1).trim();
    if (tail.length > 0 && !/^\d/u.test(tail)) {
      note = note ? `${note}, ${tail}` : tail;
      rest = rest.slice(0, comma).trim();
    }
  }

  const leading = rest.match(LEADING_QUANTITY);
  const wort = rest.match(LEADING_NUMBER_WORD);
  if (leading?.[1] && leading[0].trim().length > 0 && rest.slice(leading[0].length).trim().length > 0) {
    quantity = toNumber(leading[1]);
    const alias = leading[2]?.toLocaleLowerCase("de-DE");
    unit = alias ? (UNIT_ALIASES[alias] ?? null) : null;
    rest = rest.slice(leading[0].length).trim();
  } else if (wort?.[1] && rest.slice(wort[0].length).trim().length > 0) {
    quantity = ZAHLWOERTER[wort[1].toLocaleLowerCase("de-DE")] ?? null;
    const alias = wort[2]?.toLocaleLowerCase("de-DE");
    unit = alias ? (UNIT_ALIASES[alias] ?? null) : null;
    rest = rest.slice(wort[0].length).trim();
  } else {
    const trailing = rest.match(TRAILING_QUANTITY);
    if (trailing?.[1]) {
      quantity = toNumber(trailing[1]);
      rest = rest.slice(0, trailing.index).trim();
    }
  }

  const name = rest.replace(/^[-–\s]+|[-\s]+$/gu, "").trim();

  return {
    name: name.length > 0 ? name : raw.trim(),
    normalizedName: normalizeName(name.length > 0 ? name : raw),
    quantity,
    unit,
    note,
  };
}

export function isUnit(value: string): value is Unit {
  return (UNITS as readonly string[]).includes(value);
}
